import { useEffect, useState } from "react";
import useAssetService from "../../services/AssetService";
import { StatusMessage } from "../../types/StatusMessage";
import PageTitle from "../../components/PageTitle";
import { Card } from "@mui/material";

export default function AdminStatusPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [initLoad, setInitLoad] = useState(false);
  const [isClean, setIsClean] = useState(true);
  const { setStatusMessage, getStatusMessage, deleteStatusMessage } = useAssetService();

  // set an explicit initial value to avoid undefined unions
  const [statusMessage, setStatusMessageState] = useState<StatusMessage | null>(null);

  const [formData, setFormData] = useState({
    message: "",
    buttonText: "",
    buttonUrl: "",
    isWarning: false,
  });

  const fetchStatusMessage = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const message = await getStatusMessage();
      setStatusMessageState(message ?? null);
      if (message) {

        setFormData({
            message: message.message,
            buttonText: message.buttonText || "", 
            buttonUrl: message.buttonUrl || "",
            isWarning: message.isWarning,
        })
      } else {
        // ensure form is clear when there's no message
        setFormData({
            message: "",
            buttonText: "",
            buttonUrl: "",
            isWarning: false,
        });
      }
    } catch (error) {
      console.error("Error fetching status message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setIsClean(false);

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const deleteMessage = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await deleteStatusMessage();
      setStatusMessageState(null);
      // clear form too
      setFormData({
            message: "",
            buttonText: "",
            buttonUrl: "",
            isWarning: false,
        });
    } catch (error) {
      console.error("Error deleting status message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initLoad) {
        return;
    }

    setInitLoad(true);
    fetchStatusMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initLoad]);

  const save = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (isLoading) return;

    // basic validation: require a message
    if (!formData.message.trim()) return;

    setIsLoading(true);
    try {

        var newStatusMessage: StatusMessage = {
            message: formData.message.trim(),
            buttonText: formData.buttonText.trim() || null,
            buttonUrl: formData.buttonUrl.trim() || null,
            isWarning: formData.isWarning,
        };

      await setStatusMessage(newStatusMessage);

      fetchStatusMessage();
    } catch (error) {
      console.error("Error saving status message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTitle>Status message presented to users in the BeamXR Live app</PageTitle>

      {isLoading && <div>Loading...</div>}

      {!isLoading && statusMessage == null && (
        <h2 className="text-2xl">There is no current message</h2>
      )}

      {!isLoading && statusMessage && (
        <>
          <h2 className="text-2xl">Current message</h2>
          <Card>
            {/* Optional: make warnings red, info blue */}
            <div
              className={`p-4 flex items-center ${
                statusMessage.isWarning ? "bg-red-100" : "bg-blue-100"
              }`}
            >
              <h3 className="text-xl">{statusMessage.message}</h3>
              {statusMessage.buttonText && (
                <a
                  target="_blank"
                  href={statusMessage.buttonUrl!}
                  className="ml-auto text-sm px-3 py-1 bg-blue-600 text-white rounded-md"
                >
                  {statusMessage.buttonText}
                </a>
              )}
            </div>
          </Card>
          <div className="flex gap-2">
            <button
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50"
              onClick={deleteMessage}
            >
              Delete Message
            </button>
          </div>
        </>
      )}

      {!isLoading && (
        <form onSubmit={save} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="message">Message</label>
            <input
              type="text"
              name="message"
              id="message"
              className="border p-2 rounded-md text-black"
              placeholder="Enter status message"
              value={formData.message}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="buttonText">Button Text</label>
            <input
              type="text"
              id="buttonText"
              name="buttonText"
              className="border p-2 rounded-md text-black"
              placeholder="Enter button text (optional)"
              value={formData.buttonText}
              onChange={handleInputChange}
              
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="buttonUrl">Button URL</label>
            <input
              type="url"
              id="buttonUrl"
              name="buttonUrl"
              className="border p-2 rounded-md text-black"
              placeholder="Enter button URL (optional)"
              value={formData.buttonUrl}
              onChange={handleInputChange}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isWarning"
              name="isWarning"
              value={formData.isWarning.toString()}
              checked={formData.isWarning}
              onChange={(e) => setFormData({ ...formData, isWarning: e.target.checked })} 
            />
            <label htmlFor="isWarning">Is Warning</label>
          </div>

          <button
            disabled={isLoading || isClean || !formData.message.trim()}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
            type="submit"
          >
            Save Status Message
          </button>
        </form>
      )}
    </div>
  );
}
